/**
 * Node Testing Service
 * 
 * Provides functionality for testing nodes and executing workflows
 * up to a specific node for preview/debugging purposes.
 */

import { workflowApi } from '@/lib/api';

interface ExecuteUpToResponse {
  success: boolean;
  nodeOutputs: Record<string, any>;
  targetInputData: Array<{
    nodeId: string;
    nodeName: string;
    nodeType?: string;
    nodeDistance: number;
    data: any;
    sourceHandle?: string;
  }>;
  executedNodes: string[];
  error?: string;
  failedNodeId?: string;
}

export class NodeTestingService {
  /**
   * Execute a workflow up to (but not including) a specific node
   * Returns the input data that would be available to that node
   */
  static async executeUpTo(
    workflowId: string,
    targetNodeId: string,
    workflowDefinition?: any,
    includeTarget?: boolean
  ): Promise<ExecuteUpToResponse> {
    const response = await workflowApi.executeUpTo(
      workflowId,
      targetNodeId,
      workflowDefinition,
      includeTarget
    );
    return response.data;
  }

  /**
   * Test a single node with provided input data or previousNodeOutputs
   *
   * UNIFIED: Now uses ExecuteUpTo endpoint with "isolatedTest" mode
   * This consolidates all testing through a single endpoint for easier maintenance.
   */
  static async testNode(params: {
    nodeId: string;
    nodeType: string;
    nodeData: Record<string, any>;
    inputData?: Array<{ nodeId: string; label?: string; data: any }>;
    previousNodeOutputs?: Record<string, any>;
    workflowId: string;
    workflowDefinition?: any;
  }): Promise<{
    success: boolean;
    output: any;
    error?: string;
    executionTime: number;
    logs: Array<{
      level: string;
      message: string;
      timestamp: string;
    }>;
  }> {
    try {
      // Convert inputData to previousNodeOutputs format if provided
      const previousNodeOutputs = params.previousNodeOutputs || {};
      if (params.inputData && !params.previousNodeOutputs) {
        params.inputData.forEach((input: any) => {
          const nodeName = input.label || input.nodeId;
          previousNodeOutputs[nodeName] = input.data;
        });
      }

      // Use ExecuteUpTo with isolatedTest mode (consolidated endpoint)
      const response = await workflowApi.executeUpTo(
        params.workflowId,
        params.nodeId,
        params.workflowDefinition,
        false, // includeTarget (backwards compat)
        'isolatedTest', // mode
        previousNodeOutputs
      );

      const result = response.data;

      if (!result.success) {
        throw new Error(result.error || 'Node test failed');
      }

      // Extract the target node output from nodeOutputs
      const targetNodeOutput = result.nodeOutputs?.[params.nodeId];

      if (!targetNodeOutput) {
        throw new Error('No output returned for target node');
      }

      // Return in the expected format
      return {
        success: targetNodeOutput.success,
        output: targetNodeOutput.output,
        error: targetNodeOutput.error,
        executionTime: result.executionTime || 0,
        logs: targetNodeOutput.logs || []
      };
    } catch (error: any) {
      if (error.code === 'ECONNABORTED') {
        throw new Error('Node test timed out after 30 seconds. The node may be taking too long to execute or the backend may be unresponsive.');
      }
      throw error;
    }
  }
}

/**
 * React hook for node testing
 */
export function useNodeTesting() {
  return {
    executeUpTo: NodeTestingService.executeUpTo,
    testNode: NodeTestingService.testNode,
  };
}
